import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { api, money } from "../lib/data";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Check, X, Users, Receipt, ChevronDown, ChevronUp, ShieldCheck, Vote, Plus, Trash2, Lock, Mail } from "lucide-react";

function StatusBadge({ status }) {
  const map = {
    pending: "text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10",
    approved: "text-[#00B368] border-[#00B368]/40 bg-[#00B368]/10",
    rejected: "text-red-600 border-red-300 bg-red-50",
  };
  return <span className={`text-xs font-mono uppercase px-2 py-0.5 border rounded-sm ${map[status]}`}>{status}</span>;
}

function PayoutManager({ purchase, onChange }) {
  const paid = new Set(purchase.paid_weeks || []);
  const toggle = async (week) => {
    const isPaid = paid.has(week);
    const { data } = await api.post(`/admin/purchases/${purchase.id}/payout`, { week, paid: !isPaid });
    onChange(data);
  };
  return (
    <div className="bg-[#F9F9F8] border-t border-gray-200 p-4">
      <p className="text-xs font-mono uppercase text-gray-500 mb-3">Mark weekly payouts as paid · {money(purchase.weekly)}/wk · {paid.size}/52 paid</p>
      <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(13, minmax(0, 1fr))" }}>
        {Array.from({ length: 52 }, (_, i) => i + 1).map((w) => (
          <button key={w} onClick={() => toggle(w)} data-testid={`payout-toggle-${purchase.id}-${w}`}
            className={`h-7 text-[10px] font-mono rounded-sm transition-colors ${paid.has(w) ? "bg-[#00B368] text-white" : "bg-white border border-gray-200 text-gray-500 hover:border-[#00B368]"}`}>
            {w}
          </button>
        ))}
      </div>
    </div>
  );
}

function GovernanceAdmin() {
  const [proposals, setProposals] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const load = () => api.get("/proposals").then((r) => setProposals(r.data.proposals));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    await api.post("/admin/proposals", { title, description });
    setTitle(""); setDescription(""); toast.success("Proposal published for member voting"); load();
  };
  const close = async (id) => { await api.post(`/admin/proposals/${id}/close`); toast("Proposal closed"); load(); };
  const del = async (id) => { await api.delete(`/admin/proposals/${id}`); toast("Proposal deleted"); load(); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={create} className="border border-gray-200 bg-white p-5 space-y-3" data-testid="proposal-form">
        <div className="flex items-center gap-2 font-semibold text-[#0A1914]"><Plus className="h-4 w-4 text-[#00B368]" /> New proposal</div>
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-gray-500">Title</label>
          <input data-testid="proposal-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full mt-1 border border-gray-300 rounded-sm px-3 py-2.5 focus:border-[#00B368] focus:ring-1 focus:ring-[#00B368]/20 outline-none" />
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-gray-500">Description</label>
          <textarea data-testid="proposal-description" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full mt-1 border border-gray-300 rounded-sm px-3 py-2.5 focus:border-[#00B368] focus:ring-1 focus:ring-[#00B368]/20 outline-none resize-none" />
        </div>
        <button data-testid="create-proposal" className="w-full bg-[#0A1914] text-white py-2.5 rounded-sm hover:bg-[#00B368] transition-colors">Publish proposal</button>
      </form>

      <div className="lg:col-span-2 space-y-3">
        {proposals.length === 0 && <p className="text-gray-500">No proposals yet. Create one for members to vote on.</p>}
        {proposals.map((p) => {
          const total = p.yes + p.no;
          const yesPct = total ? Math.round((p.yes / total) * 100) : 0;
          return (
            <div key={p.id} className="border border-gray-200 bg-white p-5" data-testid={`admin-proposal-${p.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2"><Vote className="h-4 w-4 text-[#00B368]" /><h3 className="font-display text-lg text-[#0A1914]">{p.title}</h3></div>
                  {p.description && <p className="text-sm text-gray-600 mt-1">{p.description}</p>}
                  <p className="text-xs font-mono text-gray-500 mt-2">Yes {p.yes} · No {p.no} · {yesPct}% approve · <span className="uppercase">{p.status}</span></p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {p.status === "open" && <button onClick={() => close(p.id)} data-testid={`close-proposal-${p.id}`} className="flex items-center gap-1 border border-[#0A1914] text-sm px-3 py-1.5 rounded-sm hover:bg-[#F3F4F3] transition-colors"><Lock className="h-4 w-4" /> Close</button>}
                  <button onClick={() => del(p.id)} data-testid={`delete-proposal-${p.id}`} className="flex items-center gap-1 border border-red-300 text-red-600 text-sm px-3 py-1.5 rounded-sm hover:bg-red-50 transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ContactMessages() {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { api.get("/admin/contact-messages").then((r) => setMsgs(r.data)).catch(() => {}); }, []);
  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  return (
    <div className="border border-gray-200 bg-white" data-testid="contact-messages">
      {msgs.length === 0 && <p className="p-6 text-gray-500">No contact messages yet.</p>}
      <div className="divide-y divide-gray-200">
        {msgs.map((m) => (
          <div key={m.id} className="px-5 py-4" data-testid={`message-${m.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[#0A1914]">{m.name}</span>
                <a href={`mailto:${m.email}`} className="text-sm text-[#00B368] hover:underline flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{m.email}</a>
              </div>
              <span className="text-xs font-mono text-gray-500">{fmt(m.created_at)}</span>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{m.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data));
  const loadPurchases = () => api.get("/admin/purchases").then((r) => setPurchases(r.data));
  useEffect(() => { loadUsers(); loadPurchases(); }, []);

  const updateOne = (p) => setPurchases((prev) => prev.map((x) => (x.id === p.id ? p : x)));

  const approve = async (id) => { const { data } = await api.post(`/admin/purchases/${id}/approve`); updateOne(data); loadUsers(); toast.success("Purchase approved — member now has access"); };
  const reject = async (id) => { const { data } = await api.post(`/admin/purchases/${id}/reject`); updateOne(data); toast("Purchase rejected"); };

  const pending = purchases.filter((p) => p.status === "pending");
  const totalRevenue = purchases.filter((p) => p.status === "approved").reduce((s, p) => s + p.price, 0);

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 text-[#D4AF37] mb-2"><ShieldCheck className="h-5 w-5" /><p className="font-mono text-xs uppercase tracking-widest">Admin Console</p></div>
        <h1 className="font-display text-4xl tracking-tight text-[#0A1914] mb-8">Control Center</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="border border-gray-200 bg-white px-5 py-4"><p className="text-xs text-gray-500">Total members</p><p className="font-mono text-2xl text-[#0A1914]">{users.length}</p></div>
          <div className="border border-gray-200 bg-white px-5 py-4"><p className="text-xs text-gray-500">Total orders</p><p className="font-mono text-2xl text-[#0A1914]">{purchases.length}</p></div>
          <div className="border border-gray-200 bg-white px-5 py-4"><p className="text-xs text-gray-500">Pending approval</p><p className="font-mono text-2xl text-[#F59E0B]">{pending.length}</p></div>
          <div className="border border-gray-200 bg-white px-5 py-4"><p className="text-xs text-gray-500">Approved revenue</p><p className="font-mono text-2xl text-[#00B368]">{money(totalRevenue)}</p></div>
        </div>

        <Tabs defaultValue="purchases">
          <TabsList className="mb-6">
            <TabsTrigger value="purchases" data-testid="tab-purchases"><Receipt className="h-4 w-4 mr-2" /> Purchases</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users"><Users className="h-4 w-4 mr-2" /> Members</TabsTrigger>
            <TabsTrigger value="governance" data-testid="tab-governance"><Vote className="h-4 w-4 mr-2" /> Governance</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages"><Mail className="h-4 w-4 mr-2" /> Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="purchases">
            <div className="border border-gray-200 bg-white divide-y divide-gray-200">
              {purchases.length === 0 && <p className="p-6 text-gray-500">No purchases yet.</p>}
              {purchases.map((p) => (
                <div key={p.id} data-testid={`admin-purchase-${p.id}`}>
                  <div className="flex flex-wrap items-center gap-4 px-5 py-4">
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-semibold text-[#0A1914]">{p.course_title}</p>
                      <p className="text-xs text-gray-500">{p.user_name} · {p.user_email}</p>
                      <p className="text-xs text-gray-400 font-mono mt-0.5">TXID: {p.txid}</p>
                    </div>
                    <span className="font-mono text-[#0A1914]">{money(p.price)}</span>
                    <StatusBadge status={p.status} />
                    {p.status === "pending" ? (
                      <div className="flex gap-2">
                        <button onClick={() => approve(p.id)} data-testid={`approve-${p.id}`} className="flex items-center gap-1 bg-[#00B368] text-white text-sm px-3 py-1.5 rounded-sm hover:bg-[#009355] transition-colors"><Check className="h-4 w-4" /> Approve</button>
                        <button onClick={() => reject(p.id)} data-testid={`reject-${p.id}`} className="flex items-center gap-1 border border-red-300 text-red-600 text-sm px-3 py-1.5 rounded-sm hover:bg-red-50 transition-colors"><X className="h-4 w-4" /> Reject</button>
                      </div>
                    ) : p.status === "approved" ? (
                      <button onClick={() => setExpanded(expanded === p.id ? null : p.id)} data-testid={`manage-payouts-${p.id}`} className="flex items-center gap-1 border border-[#0A1914] text-sm px-3 py-1.5 rounded-sm hover:bg-[#F3F4F3] transition-colors">
                        Payouts {expanded === p.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    ) : null}
                  </div>
                  {expanded === p.id && p.status === "approved" && <PayoutManager purchase={p} onChange={updateOne} />}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="border border-gray-200 bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F3F4F3] text-left font-mono text-xs uppercase text-gray-500">
                  <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Courses</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`admin-user-${u.id}`}>
                      <td className="px-5 py-3 font-medium text-[#0A1914]">{u.name}</td>
                      <td className="px-5 py-3 text-gray-600">{u.email}</td>
                      <td className="px-5 py-3">{u.role === "admin" ? <span className="text-[#D4AF37] font-mono text-xs uppercase">Admin</span> : <span className="text-gray-500 font-mono text-xs uppercase">Member</span>}</td>
                      <td className="px-5 py-3 font-mono">{u.purchase_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="governance">
            <GovernanceAdmin />
          </TabsContent>

          <TabsContent value="messages">
            <ContactMessages />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
